import React, { useState, useEffect } from 'react'
import { FaFileImage } from "react-icons/fa";
import toast from "react-hot-toast";
import { IoMdCheckmark } from "react-icons/io";
import { GetUserDocuments, UploadImage, UpdateUserDocumentApi } from '../../../../Api/UserDocument';

type User = {
  id: string;
  name: string;
  email: string;
  mobile: string;
  gender: number | null;
  address: string;
  roleType: string;
};

interface Props {
  url: string;
  user: User;
}
type FileStatus = "idle" | "uploading" | "success" | "error"
interface FileState {
  id: string | null;
  file: File | null
  status: FileStatus
  progress: number
  previewUrl: string | null
}

const Document = ({
  url,
  user,
}: Props) => {
  const [aadharCard, setAadharCard] = useState<FileState>({
    id: null,
    file: null,
    status: "idle",
    progress: 0,
    previewUrl: null,
  })

  const [panCard, setPanCard] = useState<FileState>({
    id: null,
    file: null,
    status: "idle",
    progress: 0,
    previewUrl: null,
  })

  const handleApiCallClick = async () => {
    try {
      const response = await GetUserDocuments(url, user.id);
      response.forEach((doc: any) => {
        if (doc.type === "Aadhar Card") {
          setAadharCard((prev) => ({
            ...prev,
            id: doc.id,
            file: null,
            status: "success",
            progress: 100,
            previewUrl: doc.image, // Assuming the API returns the image URL
          }));
        } else if (doc.type === "Pan Card") {
          setPanCard((prev) => ({
            ...prev,
            id: doc.id,
            file: null,
            status: "success",
            progress: 100,
            previewUrl: doc.image, // Assuming the API returns the image URL
          }));
        }
      })
    } catch (error) {
      console.error("Failed to fetch user documents:", error);
    }
  };

  useEffect(() => {
    handleApiCallClick(); // Call the function inside useEffect
  }, [user]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFileState: React.Dispatch<React.SetStateAction<FileState>>,
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Revoke previous preview URL (not needed for base64, but still good to clean up state)
      setFileState((prev) => {
        return {
          ...prev,
          previewUrl: null, // remove old preview
        };
      });

      const reader = new FileReader();

      reader.onloadend = () => {
        const base64 = reader.result as string;

        setFileState((prev) => ({
          ...prev,
          file: file,
          status: "idle",
          progress: 0,
          previewUrl: base64, // Set base64 string here
        }));
      };

      reader.readAsDataURL(file); // Converts file to base64
    }
  };


  const handleImageUpload = async (FileState: FileState, setFileState: React.Dispatch<React.SetStateAction<FileState>>, type: string) => {
    setFileState((prev) => ({ ...prev, status: "uploading", progress: 0 }))


    try {
      let imageUrl = null;
      if (!FileState.file) return;
      if (FileState.file) {
        const reader = new FileReader();
        imageUrl = await new Promise<string>((resolve, reject) => {
          reader.onloadend = () => {
            if (reader.result) resolve(reader.result.toString());
            else reject("Failed to read image");
          };
          reader.readAsDataURL(FileState.file);
        });
      }

      setFileState((prev) => ({ ...prev, progress: 20 }))

      if (!FileState.id) {
        const upload = await UploadImage(
          url,
          type,
          imageUrl,
          user.id,
        )
      }else{
        const upload = await UpdateUserDocumentApi(
          url,
          FileState.id,
          imageUrl,
        )
      }      
      // setFileState((prev) => ({ ...prev, id: upload.id }))

      setFileState((prev) => ({ ...prev, status: "success", progress: 100 }))
      toast.success("Image uploaded successfully!")

    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error uploading image. Please try again.");
      setFileState((prev) => ({ ...prev, status: "error", progress: 0 }))

    }
  }

  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean
    url: string | null
    title: string
  }>({
    isOpen: false,
    url: null,
    title: "",
  })
  const getStatusColor = (status: FileStatus) => {
    switch (status) {
      case "uploading":
        return "text-blue-500"
      case "success":
        return "text-green-500"
      case "error":
        return "text-red-500"
      default:
        return "text-gray-500"
    }
  }
  const openPreview = (url: string | null, title: string) => {
    if (url) {
      setPreviewModal({
        isOpen: true,
        url,
        title,
      })
    }
  }

  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      url: null,
      title: "",
    })
  }

  React.useEffect(() => {
    return () => {
      // Cleanup preview URLs when component unmounts
      if (aadharCard.previewUrl) {
        URL.revokeObjectURL(aadharCard.previewUrl)
      }
      if (panCard.previewUrl) {
        URL.revokeObjectURL(panCard.previewUrl)
      }
    }
  }, [aadharCard.previewUrl, panCard.previewUrl])

  return (
    <div className="mt-4 p-4 border border-gray-300 rounded-md">
      <h2 className="text-2xl font-semibold mb-3">
        Documents
      </h2>

      <div className="space-y-6">
        {/* Aadhar Card Upload */}
        <div className="p-5 bg-gray-100 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <FaFileImage className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Aadhar Card</h3>
                <p className="text-sm text-gray-500">Upload Aadhar card (Image)</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {aadharCard.file && (
                <div className="flex items-center">
                  {aadharCard.status === "success" && (
                    <IoMdCheckmark className='text-green-500' />
                  )}
                  <span className={`ml-2 text-sm ${getStatusColor(aadharCard.status)}`}>
                    {aadharCard.file.name.length > 20
                      ? aadharCard.file.name.substring(0, 20) + "..."
                      : aadharCard.file.name}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <label
                  htmlFor="aadharCard"
                  className="cursor-pointer px-3 py-1.5 font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Browse
                </label>
                <input
                  id="aadharCard"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, setAadharCard)}
                />

                <button
                  type="button"
                  className="px-3 py-1.5 font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                  disabled={
                    !aadharCard.file || aadharCard.status === "uploading" || aadharCard.status === "success"
                  }
                  onClick={() => handleImageUpload(aadharCard, setAadharCard, "Aadhar Card")}
                >
                  {aadharCard.status === "uploading" ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {aadharCard.progress}%
                    </span>
                  ) : aadharCard.status === "success" ? (
                    "Uploaded"
                  ) : (
                    "Upload"
                  )}
                </button>
              </div>
            </div>
          </div>

          {aadharCard.status === "uploading" && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${aadharCard.progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Preview thumbnail */}
          {aadharCard.previewUrl && (
            <div className="mt-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-sm text-gray-700 hover:underline"
                  onClick={() => openPreview(aadharCard.previewUrl, "Aadhar Card")}
                >
                  View Preview
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Pan Card Upload */}
        <div className="p-5 bg-gray-100 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <FaFileImage className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Pan Card</h3>
                <p className="text-sm text-gray-500">Upload Pan card (Image)</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {panCard.file && (
                <div className="flex items-center">
                  {aadharCard.status === "success" && (
                    <IoMdCheckmark className='text-green-500' />
                  )}
                  <span className={`ml-2 text-sm ${getStatusColor(panCard.status)}`}>
                    {panCard.file.name.length > 20
                      ? panCard.file.name.substring(0, 20) + "..."
                      : panCard.file.name}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <label
                  htmlFor="panCard"
                  className="cursor-pointer px-3 py-1.5 font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Browse
                </label>
                <input
                  id="panCard"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, setPanCard)}
                />

                <button
                  type="button"
                  className="px-3 py-1.5 font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                  disabled={!panCard.file || panCard.status === "uploading" || panCard.status === "success"}
                  onClick={() => handleImageUpload(panCard, setPanCard, "Pan Card")}
                >
                  {panCard.status === "uploading" ? (
                    <span className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {panCard.progress}%
                    </span>
                  ) : panCard.status === "success" ? (
                    "Uploaded"
                  ) : (
                    "Upload"
                  )}
                </button>
              </div>
            </div>
          </div>

          {panCard.status === "uploading" && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${panCard.progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Preview thumbnail */}
          {panCard.previewUrl && (
            <div className="mt-4">
              <div className="flex items-center gap-2">
                {/* <div
                  className="relative w-20 h-20 border rounded-md overflow-hidden cursor-pointer"
                  onClick={() => openPreview(panCard.previewUrl, "Pan Card")}
                >
                  <img
                    src={panCard.previewUrl || "/placeholder.svg"}
                    alt="Pan Card Preview"
                    className="object-cover w-full h-full"
                  />
                </div> */}
                <button
                  type="button"
                  className="text-sm text-gray-700 hover:underline"
                  onClick={() => openPreview(panCard.previewUrl, "Pan Card")}
                >
                  View Preview
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {previewModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">{previewModal.title} Preview</h3>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 text-xl"
                onClick={closePreview}
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
              {previewModal.url && (
                <div className="relative max-h-[70vh] max-w-full">
                  <img
                    src={previewModal.url || "/placeholder.svg"}
                    alt={`${previewModal.title} Preview`}
                    className="object-contain max-h-[70vh] max-w-full"
                  />
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100"
                onClick={closePreview}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {/* <div className='m-4 gap-4'>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="flex flex-col space-y-2">
            <label className="font-medium text-black dark:text-gray-200">
              Upload Adhaar Card
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleAdhaarImageChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {AdhaarImagePreview && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Preview:</p>
                <img
                  src={AdhaarImagePreview || ""}
                  alt="Preview"
                  className="w-72 h-72 object-cover rounded-md border border-gray-300"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <label className="font-medium text-black dark:text-gray-200">
              Upload Pan Card
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePanImageChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {PanImagePreview && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Preview:</p>
                <img
                  src={PanImagePreview || ""}
                  alt="Preview"
                  className="w-72 h-72 object-cover rounded-md border border-gray-300"
                />
              </div>
            )}
          </div>
        </div>
      </div> */}

    </div>
  )
}

export default Document